import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import axios from "axios";
import { X, ChevronDown, ChevronUp, DollarSign, Save, Download, Mail, Check } from "lucide-react";

import { apiBaseUrl } from '../../../../../../services/config';
const API_BASE = apiBaseUrl;

const T = {
  bg:"#06101F", card:"#0D1B2E", card2:"#0A1628",
  border:"rgba(0,200,255,0.10)", borderH:"rgba(0,200,255,0.20)",
  cyan:"#00C8FF", purple:"#7C5CFF", green:"#00E5A3",
  amber:"#FFB800", red:"#FF4D6D", text:"#E8F0FE",
  muted:"#7A93B4", dim:"#3D5473", input:"rgba(255,255,255,0.05)",
};
const inp = (extra={}) => ({
  width:"100%", padding:"8px 12px", border:`1px solid ${T.border}`,
  borderRadius:7, background:T.input, color:T.text,
  fontFamily:"Outfit,sans-serif", fontSize:13, outline:"none",
  boxSizing:"border-box", ...extra,
});
const lbl = {
  fontSize:11, fontWeight:700, color:T.muted,
  textTransform:"uppercase", letterSpacing:"0.07em",
  display:"block", marginBottom:4,
};

const authH = () => ({
  Authorization: `Bearer ${
    sessionStorage.getItem("platform_token") ||
    localStorage.getItem("platform_token") || ""
  }`
});

const VAR_GROUPS = [
  { group:"Candidate Info", vars:[
    { key:"candidate_name",  label:"Full Name" },
    { key:"position",        label:"Position / Role" },
    { key:"department",      label:"Department" },
    { key:"reporting_to",    label:"Reporting Manager" },
    { key:"work_location",   label:"Work Location",   default:"Chennai, Tamil Nadu" },
    { key:"employment_type", label:"Employment Type", default:"Full-Time, Permanent" },
  ]},
  { group:"Compensation", vars:[
    { key:"ctc_annual",   label:"Annual CTC",         placeholder:"e.g. ₹6,00,000" },
    { key:"ctc_monthly",  label:"Monthly Gross",       placeholder:"Auto-calculated" },
    { key:"basic_salary", label:"Basic Salary/month",  placeholder:"Auto-calculated" },
    { key:"hra",          label:"HRA/month",           placeholder:"Auto-calculated" },
    { key:"allowances",   label:"Other Allowances/mo", placeholder:"Auto-calculated" },
  ]},
  { group:"Dates", vars:[
    { key:"offer_date",          label:"Offer Date",          type:"date" },
    { key:"joining_date",        label:"Joining / Start Date", type:"date" },
    { key:"acceptance_deadline", label:"Acceptance Deadline",  type:"date" },
  ]},
  { group:"Policies", vars:[
    { key:"probation_period", label:"Probation Period", default:"6 months" },
    { key:"notice_period",    label:"Notice Period",    default:"60 days"  },
    { key:"work_hours",       label:"Work Hours/day",   default:"9 hours"  },
    { key:"work_days",        label:"Work Days/week",   default:"5 days (Mon–Fri)" },
  ]},
  { group:"Leave Policy", vars:[
    { key:"leave_annual",    label:"Annual / Earned Leave",   default:"18 days/year" },
    { key:"leave_sick",      label:"Sick Leave",              default:"6 days/year"  },
    { key:"leave_casual",    label:"Casual Leave",            default:"6 days/year"  },
    { key:"leave_maternity", label:"Maternity Leave",         default:"26 weeks"     },
    { key:"leave_paternity", label:"Paternity Leave",         default:"5 days"       },
    { key:"leave_optional",  label:"Optional/Festival Leave", default:"2 days/year"  },
  ]},
  { group:"Company & HR", vars:[
    { key:"company_name",    label:"Company Name",    default:"Hirality Technologies Pvt. Ltd." },
    { key:"company_address", label:"Company Address", placeholder:"e.g. Chennai, Tamil Nadu" },
    { key:"hr_name",         label:"HR Signatory Name" },
    { key:"hr_designation",  label:"HR Designation",  default:"Human Resources Manager" },
    { key:"hr_email",        label:"HR Email" },
  ]},
];

const DEFAULT_SECTIONS = [
  { id:"header",  title:"Company Header", enabled:true,
    content:`{{company_name}}\n{{company_address}}\n{{hr_email}}` },
  { id:"intro",   title:"Introduction & Offer", enabled:true,
    content:`Date: {{offer_date}}\n\nDear {{candidate_name}},\n\nWe are delighted to offer you the position of **{{position}}** at **{{company_name}}**. This offer is extended following your successful interview process, and we look forward to you joining our growing team.\n\nPlease read this letter carefully and return a signed copy by **{{acceptance_deadline}}**.` },
  { id:"role",    title:"Role & Responsibilities", enabled:true,
    content:`**Position:** {{position}}\n**Department:** {{department}}\n**Reporting To:** {{reporting_to}}\n**Employment Type:** {{employment_type}}\n**Work Location:** {{work_location}}\n**Date of Joining:** {{joining_date}}\n\nYour role will involve responsibilities as mutually discussed during the interview process.` },
  { id:"compensation", title:"Compensation & Benefits", enabled:true,
    content:`Your annual Cost to Company (CTC) is **{{ctc_annual}}**, structured as follows:\n\n| Component             | Monthly Amount     |\n|-----------------------|--------------------|\n| Basic Salary          | {{basic_salary}}   |\n| House Rent Allowance  | {{hra}}            |\n| Other Allowances      | {{allowances}}     |\n| **Gross Monthly**     | **{{ctc_monthly}}**|\n\nAll statutory deductions (PF, ESI, PT, TDS) will be applicable as per government regulations.` },
  { id:"probation",  title:"Probation Period", enabled:true,
    content:`You will be on probation for **{{probation_period}}** from your date of joining. During this period, employment may be terminated by either party with 30 days' notice.\n\nUpon successful completion, your employment will be confirmed in writing.` },
  { id:"work_hours", title:"Work Hours & Attendance", enabled:true,
    content:`**Working Hours:** {{work_hours}} per day\n**Working Days:** {{work_days}}\n\nYou are expected to maintain regular and punctual attendance.` },
  { id:"leave", title:"Leave Policy", enabled:true,
    content:`You are entitled to the following leave benefits per calendar year:\n\n| Leave Type              | Entitlement         |\n|-------------------------|---------------------|\n| Annual / Earned Leave   | {{leave_annual}}    |\n| Sick Leave              | {{leave_sick}}      |\n| Casual Leave            | {{leave_casual}}    |\n| Maternity Leave         | {{leave_maternity}} |\n| Paternity Leave         | {{leave_paternity}} |\n| Optional/Festival Leave | {{leave_optional}}  |` },
  { id:"notice", title:"Notice Period & Termination", enabled:true,
    content:`After confirmation, either party may terminate employment by providing **{{notice_period}}** written notice or pay in lieu thereof.\n\nUpon exit, you must complete a formal handover and return all company assets.` },
  { id:"confidentiality", title:"Confidentiality & IP", enabled:true,
    content:`During and after employment, you agree to:\n\n1. Keep all proprietary information strictly confidential.\n2. Not disclose or exploit any confidential information for personal gain.\n3. Assign all intellectual property created during employment to **{{company_name}}**.\n4. Refrain from engaging in any competing business during employment.\n\nA detailed NDA will be signed during onboarding.` },
  { id:"general", title:"General Terms", enabled:true,
    content:`1. This offer is subject to satisfactory background verification.\n2. Any false declaration may result in immediate termination.\n3. This offer supersedes all prior verbal or written communications.\n4. Governed by the laws of India.` },
  { id:"acceptance", title:"Acceptance & Signature", enabled:true,
    content:`We are excited to welcome you to **{{company_name}}**.\n\nPlease sign and return this letter by **{{acceptance_deadline}}**.\n\nWarm regards,\n\n**{{hr_name}}**\n{{hr_designation}}\n{{company_name}}\n{{hr_email}}\n\n---\n\n**Candidate Acceptance**\n\nI, {{candidate_name}}, hereby accept this offer.\n\nSignature: ___________________________\n\nDate: _______________` },
];

function fillVars(text, vars) {
  return text.replace(/\{\{(\w+)\}\}/g, (_,k) => vars[k] || `{{${k}}}`);
}

function renderSection(content) {
  const lines = content.split("\n");
  const out   = [];
  let tableRows = [], inTable = false;
  for (const line of lines) {
    if (line.startsWith("|")) {
      if (!inTable) { inTable = true; tableRows = []; }
      if (!line.includes("---")) tableRows.push(line);
    } else {
      if (inTable) {
        const hdrs = tableRows[0].split("|").filter(c=>c.trim());
        const rows = tableRows.slice(1);
        out.push(`<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px;"><thead><tr>${
          hdrs.map(h=>`<th style="padding:8px 12px;background:#f0f0f0;border:1px solid #ddd;text-align:left;">${h.trim().replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>")}</th>`).join("")
        }</tr></thead><tbody>${
          rows.map(r=>`<tr>${r.split("|").filter(c=>c.trim()).map(c=>`<td style="padding:8px 12px;border:1px solid #ddd;">${c.trim().replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>")}</td>`).join("")}</tr>`).join("")
        }</tbody></table>`);
        inTable = false; tableRows = [];
      }
      let l = line.replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>");
      if (/^\d+\./.test(l))      out.push(`<li style="margin:4px 0 4px 20px;">${l.replace(/^\d+\.\s*/,"")}</li>`);
      else if (l.trim()==="---") out.push(`<hr style="border:none;border-top:1px solid #ccc;margin:16px 0;"/>`);
      else if (l.trim()==="")    out.push(`<div style="height:8px;"></div>`);
      else                       out.push(`<p style="margin:4px 0;line-height:1.75;">${l}</p>`);
    }
  }
  return out.join("");
}

// ── Memoized section editor ───────────────────────────────────────
const SectionEditor = memo(({ section, onUpdate, onToggle, onToggleExpand, expanded }) => {
  const [localContent, setLocalContent] = useState(section.content);
  useEffect(() => { setLocalContent(section.content); }, [section.id]);
  const handleChange = useCallback((e) => setLocalContent(e.target.value), []);
  const handleBlur   = useCallback(() => onUpdate(section.id, localContent),
    [section.id, localContent, onUpdate]);

  return (
    <div style={{ marginBottom:10, border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ padding:"10px 14px", background:T.card2,
        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <input type="checkbox" checked={section.enabled}
            onChange={()=>onToggle(section.id)}
            style={{ width:15, height:15, cursor:"pointer" }}/>
          <span style={{ fontSize:13, fontWeight:700, color:section.enabled?T.text:T.dim }}>
            {section.title}
          </span>
        </div>
        {section.enabled && (
          <button onClick={()=>onToggleExpand(section.id)}
            style={{ border:"none", background:"none", color:T.muted, cursor:"pointer",
              display:"flex", alignItems:"center", gap:4, fontSize:12, fontFamily:"Outfit,sans-serif" }}>
            {expanded ? <><ChevronUp size={13}/>Collapse</> : <><ChevronDown size={13}/>Edit</>}
          </button>
        )}
      </div>
      {section.enabled && expanded && (
        <div style={{ padding:12 }}>
          <textarea value={localContent} onChange={handleChange} onBlur={handleBlur}
            rows={10} style={{ ...inp(), resize:"vertical", lineHeight:1.7,
              fontFamily:"'JetBrains Mono',monospace", fontSize:12 }}/>
          <div style={{ fontSize:11, color:T.dim, marginTop:4 }}>
            Use <code style={{color:T.cyan}}>{"{{variable}}"}</code> for dynamic values ·
            <code style={{color:T.muted}}> **bold**</code> · pipe tables · numbered lists
          </div>
        </div>
      )}
      {section.enabled && !expanded && (
        <div style={{ padding:"8px 14px", fontSize:12, color:T.muted,
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
          {section.content.replace(/\*\*/g,"").split("\n")[0]}…
        </div>
      )}
    </div>
  );
});

// ── VarField ──────────────────────────────────────────────────────
const VarField = ({ v, vars, setV, departments, designations, allEmployees }) => {
  if (v.key === "reporting_to" && allEmployees.length > 0) {
    return (
      <div style={{ marginBottom:10 }}>
        <label style={lbl}>{v.label}</label>
        <select value={vars[v.key]||""} onChange={e=>setV(v.key,e.target.value)} style={inp({cursor:"pointer"})}>
          <option value="" style={{background:"#0D1B2E"}}>— Select Manager —</option>
          {allEmployees.map(e=>(
            <option key={e.id} value={`${e.first_name} ${e.last_name}`} style={{background:"#0D1B2E"}}>
              {e.first_name} {e.last_name}{e.designation?.title?` — ${e.designation.title}`:""}
            </option>
          ))}
        </select>
      </div>
    );
  }
  if (v.key === "position" && designations.length > 0) {
    return (
      <div style={{ marginBottom:10 }}>
        <label style={lbl}>{v.label}</label>
        <select value={vars[v.key]||""} onChange={e=>setV(v.key,e.target.value)} style={inp({cursor:"pointer"})}>
          <option value="" style={{background:"#0D1B2E"}}>— Select Designation —</option>
          {designations.map(d=><option key={d.id} value={d.title} style={{background:"#0D1B2E"}}>{d.title}</option>)}
        </select>
      </div>
    );
  }
  if (v.key === "department" && departments.length > 0) {
    return (
      <div style={{ marginBottom:10 }}>
        <label style={lbl}>{v.label}</label>
        <select value={vars[v.key]||""} onChange={e=>setV(v.key,e.target.value)} style={inp({cursor:"pointer"})}>
          <option value="" style={{background:"#0D1B2E"}}>— Select Department —</option>
          {departments.map(d=><option key={d.id} value={d.name} style={{background:"#0D1B2E"}}>{d.name}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div style={{ marginBottom:10 }}>
      <label style={lbl}>{v.label}</label>
      <input type={v.type||"text"} value={vars[v.key]||""}
        placeholder={v.placeholder||`Enter ${v.label.toLowerCase()}`}
        onChange={e=>setV(v.key,e.target.value)}
        style={inp({ colorScheme:v.type==="date"?"dark":undefined })}/>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const OfferLetterGenerator = ({ iv, onClose, flash }) => {
  const companyLogo = sessionStorage.getItem("company_logo") ||
                      localStorage.getItem("company_logo") || null;

  const [step,        setStep]       = useState("vars");
  const [sections,    setSections]   = useState(DEFAULT_SECTIONS);
  const [expanded,    setExpanded]   = useState({});
  const [sending,     setSending]    = useState(false);
  const [saving,      setSaving]     = useState(false);
  const [savedId,     setSavedId]    = useState(null);
  const [templateSaved, setTemplateSaved] = useState(false);
  const [converting,  setConverting] = useState(false);
  const [converted,   setConverted]  = useState(false);

  const [departments,      setDepartments]      = useState([]);
  const [designations,     setDesignations]     = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [selectedStructure,setSelectedStructure]= useState("");
  const [allEmployees,     setAllEmployees]     = useState([]);

  // ── Init vars ─────────────────────────────────────────────────
  const [vars, setVars] = useState(() => {
    const d = {};
    VAR_GROUPS.forEach(g=>g.vars.forEach(v=>{ d[v.key]=v.default||""; }));
    d.candidate_name = `${iv.first_name||""} ${iv.last_name||""}`.trim();
    d.position       = iv.position || "";
    d.hr_email       = iv.email    || "";
    d.offer_date     = new Date().toISOString().split("T")[0];
    return d;
  });
  const setV = useCallback((k,v) => setVars(prev=>({...prev,[k]:v})), []);

  // ── Load company template + remote data ───────────────────────
  useEffect(() => {
    const h = { headers: authH() };

    // Load company's saved template first
    axios.get(`${API_BASE}/offer-letter-settings/`, h)
      .then(res => {
        if (res.data.sections) {
          try {
            const savedSections = JSON.parse(res.data.sections);
            setSections(savedSections);
          } catch(e) {}
        }
        if (res.data.variables) {
          try {
            const savedVars = JSON.parse(res.data.variables);
            // Merge saved vars with candidate-specific values
            setVars(prev => ({
              ...savedVars,
              candidate_name: prev.candidate_name,
              position:       prev.position       || savedVars.position       || "",
              hr_email:       prev.hr_email        || savedVars.hr_email       || "",
              offer_date:     prev.offer_date,
            }));
          } catch(e) {}
        }
      }).catch(() => {}); // no template saved yet — use defaults

    // Load remote data
    Promise.allSettled([
      axios.get(`${API_BASE}/departments/`, h),
      axios.get(`${API_BASE}/designations/`, h),
      axios.get(`${API_BASE}/salary-structures/`, h),
      axios.get(`${API_BASE}/employees/`, h),
    ]).then(([dRes, desRes, salRes, empRes]) => {
      if (dRes.status==="fulfilled")   setDepartments(dRes.value.data||[]);
      if (desRes.status==="fulfilled") setDesignations(desRes.value.data||[]);
      if (salRes.status==="fulfilled") setSalaryStructures(salRes.value.data||[]);
      if (empRes.status==="fulfilled") setAllEmployees(empRes.value.data||[]);
    });
  }, []);

  // ── Auto-calc compensation ────────────────────────────────────
  const calcCompensation = useCallback(async (structureId, ctcValue) => {
    if (!structureId || !ctcValue) return;
    const numCTC = parseFloat(ctcValue.replace(/[^0-9.]/g,""));
    if (isNaN(numCTC) || numCTC <= 0) return;
    try {
      const res = await axios.get(
        `${API_BASE}/salary-structures/preview?structure_id=${structureId}&ctc=${numCTC}`,
        { headers: authH() }
      );
      const data    = res.data;
      const fmt     = (n) => n ? `₹${Math.round(n).toLocaleString("en-IN")}` : "";
      const monthly = numCTC / 12;
      setVars(prev => ({
        ...prev,
        ctc_monthly:  fmt(monthly),
        basic_salary: fmt(data.basic || data.Basic || monthly * 0.40),
        hra:          fmt(data.hra   || data.HRA   || monthly * 0.20),
        allowances:   fmt(data.special || data["Special Allowance"] || monthly * 0.15),
      }));
    } catch(e) {
      const monthly = parseFloat(ctcValue.replace(/[^0-9.]/g,"")) / 12;
      if (!isNaN(monthly)) {
        const fmt = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
        setVars(prev => ({
          ...prev,
          ctc_monthly:  fmt(monthly),
          basic_salary: fmt(monthly * 0.40),
          hra:          fmt(monthly * 0.20),
          allowances:   fmt(monthly * 0.15),
        }));
      }
    }
  }, []);

  useEffect(() => {
    if (vars.ctc_annual) calcCompensation(selectedStructure, vars.ctc_annual);
  }, [vars.ctc_annual, selectedStructure]);

  // ── Section callbacks ─────────────────────────────────────────
  const updateContent  = useCallback((id, val) =>
    setSections(prev=>prev.map(s=>s.id===id?{...s,content:val}:s)), []);
  const toggleSection  = useCallback((id) =>
    setSections(prev=>prev.map(s=>s.id===id?{...s,enabled:!s.enabled}:s)), []);
  const toggleExpand   = useCallback((id) =>
    setExpanded(prev=>({...prev,[id]:!prev[id]})), []);

  // ── Build HTML (same as preview) ──────────────────────────────
  const buildHtml = useCallback(() => {
    const logo = companyLogo
      ? `<div style="text-align:center;margin-bottom:16px;"><img src="${companyLogo}" style="height:70px;max-width:260px;object-fit:contain;"/></div>` : "";
    const body = sections.filter(s=>s.enabled).map(s => {
      const filled = fillVars(s.content, vars);
      return `<div style="margin-bottom:28px;">
        ${s.id!=="header"&&s.id!=="acceptance"
          ? `<h3 style="font-size:15px;font-weight:700;color:#0D1B2E;border-bottom:2px solid #0D1B2E;padding-bottom:6px;margin:0 0 12px;">${s.title}</h3>` : ""}
        <div>${renderSection(filled)}</div>
      </div>`;
    }).join("");
    return `<div style="font-family:Georgia,serif;max-width:750px;margin:0 auto;padding:50px 60px;color:#111;font-size:14px;line-height:1.7;background:#fff;">${logo}${body}</div>`;
  }, [sections, vars, companyLogo]);

  // ── Save company template (sections + policy vars) ────────────
  const saveTemplate = useCallback(async () => {
    setSaving(true);
    try {
      // Save sections + non-candidate vars (policy, company, leave)
      const templateVars = {};
      VAR_GROUPS.forEach(g => {
        if (!["Candidate Info","Dates"].includes(g.group)) {
          g.vars.forEach(v => { templateVars[v.key] = vars[v.key] || ""; });
        }
      });
      await axios.post(`${API_BASE}/offer-letter-settings/`, {
        sections:  JSON.stringify(sections),
        variables: JSON.stringify(templateVars),
      }, { headers: authH() });
      setTemplateSaved(true);
      setTimeout(() => setTemplateSaved(false), 3000);
      flash("✅ Template saved — will load automatically next time");
    } catch(e) {
      flash("❌ Could not save template: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }, [sections, vars, flash]);

  // ── Save offer letter to DB ───────────────────────────────────
  const saveToDb = useCallback(async () => {
    if (savedId) return savedId;
    setSaving(true);
    try {
      const html = buildHtml();
      const res  = await axios.post(`${API_BASE}/offer-letters/`, {
        interview_id:    iv.id       || null,
        candidate_name:  vars.candidate_name || `${iv.first_name||""} ${iv.last_name||""}`.trim(),
        candidate_email: iv.email    || "",
        position:        vars.position        || "",
        department:      vars.department      || "",
        joining_date:    vars.joining_date    || "",
        ctc:             vars.ctc_annual      || "",
        accept_deadline: vars.acceptance_deadline || "",
        company_name:    vars.company_name    || "Hirality",
        company_address: vars.company_address || "",
        hr_name:         vars.hr_name         || "",
        hr_designation:  vars.hr_designation  || "",
        html_content:    html,
      }, { headers: authH() });
      const id = res.data?.id;
      setSavedId(id);
      return id;
    } catch(e) {
      flash("⚠️ Could not save: " + (e.response?.data?.detail || e.message));
      return null;
    } finally { setSaving(false); }
  }, [savedId, vars, iv, buildHtml, flash]);

  // ── Download: print the preview HTML ─────────────────────────
  const downloadPdf = useCallback(async () => {
    // Save to DB first so it appears in the list
    const id = await saveToDb();
    if (id) flash(`✅ Saved to Offer Letters #${id}`);

    // Print the exact HTML shown in preview
    const html = buildHtml();
    const w    = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Offer Letter — ${vars.candidate_name || "Candidate"}</title>
  <style>
    @media print {
      body { margin: 0; }
      @page { margin: 1.5cm; size: A4; }
    }
    body { margin: 0; background: #fff; }
  </style>
</head>
<body>${html}</body>
</html>`);
    w.document.close();
    w.onload = () => { w.focus(); w.print(); };
  }, [saveToDb, buildHtml, vars.candidate_name, flash]);

  // ── Send email ────────────────────────────────────────────────
  const sendEmail = useCallback(async () => {
    if (!iv.email) { flash("❌ No candidate email on record"); return; }
    setSending(true);
    try {
      const id = await saveToDb();
      if (!id) { flash("❌ Could not save offer letter"); return; }

      await axios.post(`${API_BASE}/offer-letters/${id}/send-email`, {}, {
        headers: authH()
      });
      flash("✅ Offer letter sent to " + iv.email);
    } catch(e) {
      flash("❌ " + (e.response?.data?.detail || "Send failed"));
    } finally { setSending(false); }
  }, [saveToDb, iv, flash]);

  // ── Convert to employee ───────────────────────────────────────
  const convertToEmployee = useCallback(async () => {
    setConverting(true);
    try {
      const token = sessionStorage.getItem("platform_token")||localStorage.getItem("platform_token")||"";
      const h     = { headers:{ Authorization:`Bearer ${token}` } };
      const dept  = departments.find(d=>d.name===vars.department);
      const desig = designations.find(d=>d.title===vars.position);
      const empRes = await axios.post(`${API_BASE}/employees/`, {
        first_name:      (vars.candidate_name||"").split(" ")[0] || iv.first_name,
        last_name:       (vars.candidate_name||"").split(" ").slice(1).join(" ") || iv.last_name || "",
        email:           iv.email || "",
        phone:           iv.phone || "",
        department_id:   dept?.id  || null,
        designation_id:  desig?.id || null,
        date_of_joining: vars.joining_date || new Date().toISOString().split("T")[0],
        status:          "Active",
      }, h);
      const empId = empRes.data?.id;
      if (empId && selectedStructure && vars.ctc_annual) {
        const numCTC = parseFloat(vars.ctc_annual.replace(/[^0-9.]/g,""))||0;
        if (numCTC > 0) {
          await axios.post(`${API_BASE}/salary-structures/assign`, {
            employee_id: empId, structure_id: parseInt(selectedStructure),
            ctc: numCTC, effective_from: vars.joining_date || new Date().toISOString().split("T")[0],
          }, h).catch(()=>{});
        }
      }
      if (iv.id) {
        await axios.put(`${API_BASE}/interviews-v2/${iv.id}`,
          { status:"SELECTED", notes:`Converted to employee ID: ${empId}` }, h).catch(()=>{});
      }
      setConverted(true);
      flash(`✅ ${vars.candidate_name} added as employee!`);
    } catch(e) {
      flash("❌ "+(e.response?.data?.detail||"Conversion failed"));
    } finally { setConverting(false); }
  }, [vars, iv, departments, designations, selectedStructure, flash]);

  const unfilled = (() => {
    const allContent = sections.filter(s=>s.enabled).map(s=>s.content).join(" ");
    return [...new Set((allContent.match(/\{\{(\w+)\}\}/g)||[])
      .map(m=>m.replace(/[{}]/g,"")))].filter(k=>!vars[k]);
  })();

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.80)", zIndex:2000,
      display:"flex", alignItems:"stretch", justifyContent:"center",
      backdropFilter:"blur(5px)" }}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{ background:T.card, border:`1px solid ${T.borderH}`,
        width:"100%", maxWidth:1200, margin:"20px auto", borderRadius:16,
        display:"flex", flexDirection:"column",
        boxShadow:"0 24px 80px rgba(0,0,0,0.7)", overflow:"hidden" }}>

        {/* ── Header ─────────────────────────────────────────── */}
        <div style={{ padding:"14px 22px", background:T.card2,
          borderBottom:`1px solid ${T.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div>
              <div style={{ fontSize:11, color:T.green, fontWeight:700,
                letterSpacing:"0.1em", textTransform:"uppercase" }}>OFFER LETTER GENERATOR</div>
              <div style={{ fontSize:14, fontWeight:800, color:T.text, marginTop:2 }}>
                {vars.candidate_name||"Candidate"} — {vars.position||"Position"}
              </div>
            </div>
            {savedId && (
              <div style={{ fontSize:11, color:T.green, padding:"3px 10px",
                borderRadius:6, border:`1px solid ${T.green}30`, background:`${T.green}10` }}>
                ✅ Saved #{savedId}
              </div>
            )}
            {/* Step tabs */}
            <div style={{ display:"flex", gap:4, marginLeft:8 }}>
              {[{key:"vars",label:"1  Variables"},{key:"edit",label:"2  Edit Sections"},{key:"preview",label:"3  Preview"}].map(s=>(
                <button key={s.key} onClick={()=>setStep(s.key)}
                  style={{ padding:"6px 16px", borderRadius:8, cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700, border:"none",
                    background:step===s.key?`linear-gradient(135deg,${T.cyan},${T.purple})`:"rgba(255,255,255,0.06)",
                    color:step===s.key?"#06101F":T.muted }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {unfilled.length > 0 && step!=="vars" && (
              <div style={{ fontSize:11, color:T.amber, padding:"4px 10px",
                borderRadius:6, border:`1px solid ${T.amber}30`, background:`${T.amber}10` }}>
                ⚠️ {unfilled.length} unfilled
              </div>
            )}

            {/* Save template (policy/company config) */}
            <button onClick={saveTemplate} disabled={saving}
              title="Save sections & policy as company default template"
              style={{ padding:"7px 14px", borderRadius:8, cursor:"pointer",
                border:`1px solid ${templateSaved ? T.green+"60" : T.border}`,
                background: templateSaved ? `${T.green}10` : "none",
                color: templateSaved ? T.green : T.muted,
                fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700,
                display:"flex", alignItems:"center", gap:5,
                opacity: saving ? 0.6 : 1 }}>
              {templateSaved ? <><Check size={12}/>Saved!</> : <><Save size={12}/>Save Template</>}
            </button>

            {/* Download (browser print of exact preview) */}
            <button onClick={downloadPdf} disabled={saving}
              style={{ padding:"7px 14px", borderRadius:8, cursor:"pointer",
                border:`1px solid ${T.border}`, background:"none", color:T.muted,
                fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700,
                display:"flex", alignItems:"center", gap:5,
                opacity: saving ? 0.6 : 1 }}>
              <Download size={12}/>
              Download PDF
            </button>

            {/* Send email */}
            {iv.email && (
              <button onClick={sendEmail} disabled={sending}
                style={{ padding:"7px 14px", borderRadius:8, cursor:"pointer", border:"none",
                  background:`linear-gradient(135deg,${T.green},${T.cyan})`,
                  color:"#06101F", fontFamily:"Outfit,sans-serif",
                  fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:5,
                  opacity:sending?0.7:1 }}>
                <Mail size={12}/>
                {sending?"Sending…":"Send Email + PDF"}
              </button>
            )}

            <button onClick={onClose}
              style={{ border:"none", background:"rgba(255,255,255,0.06)", borderRadius:"50%",
                width:30, height:30, cursor:"pointer", display:"flex",
                alignItems:"center", justifyContent:"center", color:T.muted }}>
              <X size={14}/>
            </button>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

          {/* STEP 1 — Variables */}
          {step==="vars" && (
            <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
              {salaryStructures.length > 0 && (
                <div style={{ marginBottom:20, padding:"14px 18px",
                  background:`${T.cyan}08`, border:`1px solid ${T.border}`, borderRadius:10 }}>
                  <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
                    textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
                    <DollarSign size={11} style={{display:"inline",marginRight:4}}/>
                    Salary Structure (auto-calculates compensation)
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div>
                      <label style={lbl}>Select Structure</label>
                      <select value={selectedStructure} onChange={e=>setSelectedStructure(e.target.value)}
                        style={inp({cursor:"pointer"})}>
                        <option value="" style={{background:T.card}}>— Choose salary structure —</option>
                        {salaryStructures.map(s=>(
                          <option key={s.id} value={s.id} style={{background:T.card}}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Annual CTC</label>
                      <input type="text" value={vars.ctc_annual||""}
                        placeholder="e.g. 600000 or ₹6,00,000"
                        onChange={e=>setV("ctc_annual",e.target.value)} style={inp()}/>
                    </div>
                  </div>
                  {vars.basic_salary && (
                    <div style={{ marginTop:10, display:"flex", gap:12, flexWrap:"wrap" }}>
                      {[["Basic",vars.basic_salary],["HRA",vars.hra],["Allowances",vars.allowances],["Monthly",vars.ctc_monthly]].map(([l,v])=>v&&(
                        <div key={l} style={{ padding:"4px 12px", borderRadius:6,
                          background:`${T.green}10`, border:`1px solid ${T.green}30`, fontSize:12 }}>
                          <span style={{ color:T.muted }}>{l}: </span>
                          <span style={{ color:T.green, fontWeight:700 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:24 }}>
                {VAR_GROUPS.map(g=>(
                  <div key={g.group}>
                    <div style={{ fontSize:11, fontWeight:700, color:T.cyan,
                      textTransform:"uppercase", letterSpacing:"0.1em",
                      marginBottom:12, paddingBottom:6, borderBottom:`1px solid ${T.border}` }}>
                      {g.group}
                    </div>
                    {g.vars.map(v=>(
                      <VarField key={v.key} v={v} vars={vars} setV={setV}
                        departments={departments} designations={designations}
                        allEmployees={allEmployees}/>
                    ))}
                  </div>
                ))}
              </div>

              <div style={{ marginTop:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:12, color:T.muted }}>
                  💡 Click <strong style={{color:T.cyan}}>Save Template</strong> to preserve
                  sections & policy defaults for all future offer letters
                </div>
                <button onClick={()=>setStep("edit")}
                  style={{ padding:"10px 28px", borderRadius:9, border:"none",
                    background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                    color:"#06101F", fontFamily:"Outfit,sans-serif",
                    fontSize:13, fontWeight:800, cursor:"pointer" }}>
                  Next: Edit Sections →
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Edit sections */}
          {step==="edit" && (
            <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
              <div style={{ marginBottom:16, padding:"12px 14px",
                background:T.card2, borderRadius:10, border:`1px solid ${T.border}` }}>
                <div style={{ fontSize:11, color:T.muted, fontWeight:700,
                  marginBottom:8, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                  Click variable to copy
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                  {VAR_GROUPS.flatMap(g=>g.vars).map(v=>(
                    <button key={v.key}
                      onClick={()=>navigator.clipboard.writeText(`{{${v.key}}}`)}
                      title={`Value: ${vars[v.key]||"(empty)"}`}
                      style={{ padding:"3px 9px", borderRadius:100, cursor:"pointer",
                        fontFamily:"'JetBrains Mono',monospace", fontSize:11,
                        border:`1px solid ${vars[v.key]?T.green+"40":T.amber+"40"}`,
                        background:vars[v.key]?`${T.green}10`:`${T.amber}08`,
                        color:vars[v.key]?T.green:T.amber }}>
                      {`{{${v.key}}}`}
                    </button>
                  ))}
                </div>
              </div>

              {sections.map(s=>(
                <SectionEditor key={s.id} section={s} expanded={!!expanded[s.id]}
                  onUpdate={updateContent} onToggle={toggleSection} onToggleExpand={toggleExpand}/>
              ))}

              <div style={{ marginTop:16, textAlign:"right" }}>
                <button onClick={()=>setStep("preview")}
                  style={{ padding:"10px 28px", borderRadius:9, border:"none",
                    background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                    color:"#06101F", fontFamily:"Outfit,sans-serif",
                    fontSize:13, fontWeight:800, cursor:"pointer" }}>
                  Next: Preview →
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Preview */}
          {step==="preview" && (
            <div style={{ flex:1, overflowY:"auto", background:"#e8e8e8", padding:24 }}>
              <div style={{ background:"#fff", maxWidth:750, margin:"0 auto",
                borderRadius:4, boxShadow:"0 4px 24px rgba(0,0,0,0.15)" }}
                dangerouslySetInnerHTML={{ __html: buildHtml() }}/>
              {unfilled.length > 0 && (
                <div style={{ maxWidth:750, margin:"14px auto", padding:"10px 16px",
                  background:`${T.amber}15`, border:`1px solid ${T.amber}40`,
                  borderRadius:8, fontSize:12, color:T.amber }}>
                  ⚠️ Unfilled: {unfilled.map(k=>(
                    <code key={k} style={{ marginLeft:6, padding:"1px 6px",
                      background:`${T.amber}20`, borderRadius:4 }}>{`{{${k}}}`}</code>
                  ))}
                  <button onClick={()=>setStep("vars")}
                    style={{ marginLeft:10, background:"none", border:"none",
                      color:T.cyan, cursor:"pointer", fontSize:12, textDecoration:"underline" }}>
                    Fill now
                  </button>
                </div>
              )}
              {/* Action bar below preview */}
              <div style={{ maxWidth:750, margin:"14px auto", display:"flex", gap:10 }}>
                <button onClick={downloadPdf}
                  style={{ flex:1, padding:"10px 0", borderRadius:9, cursor:"pointer",
                    border:`1px solid rgba(0,200,255,0.3)`, background:"none",
                    color:"#00C8FF", fontFamily:"Outfit,sans-serif", fontSize:13,
                    fontWeight:700, display:"flex", alignItems:"center",
                    justifyContent:"center", gap:6 }}>
                  <Download size={14}/> Download PDF
                </button>
                {iv.email && (
                  <button onClick={sendEmail} disabled={sending}
                    style={{ flex:1, padding:"10px 0", borderRadius:9, cursor:"pointer",
                      border:"none", background:"linear-gradient(135deg,#00E5A3,#00C8FF)",
                      color:"#06101F", fontFamily:"Outfit,sans-serif", fontSize:13,
                      fontWeight:700, display:"flex", alignItems:"center",
                      justifyContent:"center", gap:6, opacity: sending?0.7:1 }}>
                    <Mail size={14}/> {sending?"Sending…":"Send Email + PDF"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferLetterGenerator;